/**
 * Creative Asset Library (M7) — the permanent memory of every marketing asset.
 *
 * Grid / list / lineage view modes; Smart Collections rail; universal search;
 * asset drawer with 5 tabs (Overview / Preview / Lineage / Relationships /
 * Metadata); Campaign Playbook export CTA.
 */
import { useState } from "react";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import {
  Archive, BookOpen, Bot, CheckCircle, Eye, FileText, Folder, Grid, Image as ImageIcon, Layout,
  List as ListIcon, Loader2, Mail, Menu, MessageSquare, Search, Send, Sparkles, TrendingUp, UserCheck, Video,
} from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { useCurrentWorkspace } from "@/entities/workspace";
import {
  assignTags, exportPlaybook, getAssetMedia, getEnvelope, getLineage,
  getRelationships, listCollections, listLibraryPage, resolveCollectionPage, STATUS_TONE, universalSearch,
  uploadFile, type AssetCard, type AssetEnvelope, type LineageNode, type Playbook, type Relationships,
  type LibraryPageResponse, type SmartCollection,
} from "@/entities/library";
import { Button } from "@/shared/ui/button";
import { config } from "@/shared/config";
import { Input } from "@/shared/ui/input";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/shared/ui/sheet";
import { Skeleton } from "@/shared/ui/skeleton";
import { ApiError } from "@/shared/api/http";
import { useIsMobile } from "@/shared/hooks/useIsMobile";

const ICON_MAP: Record<string, any> = {
  sparkles: Sparkles, send: Send, eye: Eye, "check-circle": CheckCircle,
  video: Video, image: ImageIcon, mail: Mail, "book-open": BookOpen, layout: Layout,
  "file-text": FileText, bot: Bot, "user-check": UserCheck, "trending-up": TrendingUp,
  archive: Archive, folder: Folder, "message-square": MessageSquare,
};

type ViewMode = "grid" | "list" | "lineage";

export default function LibraryPage(): JSX.Element {
  const workspace = useCurrentWorkspace();
  const [searchParams] = useSearchParams();
  const [view, setView] = useState<ViewMode>("grid");
  const [activeSlug, setActiveSlug] = useState<string | null>(() => searchParams.get("collection"));
  const [search, setSearch] = useState("");
  const [openAssetId, setOpenAssetId] = useState<string | null>(null);
  const [collectionsOpen, setCollectionsOpen] = useState(false);
  const [pageLimit, setPageLimit] = useState(100);

  const workspaceId = workspace?.id;
  const queries = useQueries({
    queries: [
      { queryKey: ["lib", "list-page", workspaceId, pageLimit], queryFn: () => listLibraryPage(workspaceId!, pageLimit), enabled: !!workspaceId },
      { queryKey: ["lib", "collections", workspaceId], queryFn: () => listCollections(workspaceId!), enabled: !!workspaceId },
      {
        queryKey: ["lib", "collection-page", workspaceId, activeSlug, pageLimit],
        queryFn: () => resolveCollectionPage(workspaceId!, activeSlug!, pageLimit),
        enabled: !!workspaceId && !!activeSlug,
      },
      {
        queryKey: ["lib", "search", workspaceId, search],
        queryFn: () => universalSearch(workspaceId!, { q: search }),
        enabled: !!workspaceId && search.trim().length > 1,
      },
    ],
  });
  const [listQ, collQ, resolveQ, searchQ] = queries;
  const collections: SmartCollection[] = (collQ.data as SmartCollection[] | undefined) ?? [];
  const listPage = listQ.data as LibraryPageResponse | undefined;
  const collectionPage = resolveQ.data as LibraryPageResponse | undefined;
  const rowsFromResolve = collectionPage?.results ?? [];
  const rowsFromList = listPage?.results ?? [];
  const rowsFromSearch = (searchQ.data?.results as AssetCard[] | undefined) ?? [];
  const searchActive = search.trim().length > 1;
  const rows: AssetCard[] = searchActive ? rowsFromSearch : activeSlug ? rowsFromResolve : rowsFromList;
  const inventoryTotal = searchActive
    ? (searchQ.data?.total ?? rowsFromSearch.length)
    : activeSlug
      ? (collectionPage?.total ?? rowsFromResolve.length)
      : (listPage?.total ?? rowsFromList.length);
  const canLoadMore = !searchActive && rows.length < inventoryTotal;

  const pinned = collections.filter((c) => c.is_pinned);
  const other = collections.filter((c) => !c.is_pinned);

  if (!workspace) {
    return (
      <div data-testid="library-page" className="mx-auto max-w-3xl px-6 py-16 text-center">
        <div className="font-display text-lg font-semibold">Select a workspace</div>
      </div>
    );
  }

  return (
    <div data-testid="library-page" className="mx-auto max-w-[1400px] gap-4 px-4 py-6 sm:px-6 lg:grid lg:grid-cols-[260px_minmax(0,1fr)]">
      {/* ── Sidebar ─────────────────────────────────────────────── */}
      <aside data-testid="library-collection-rail" className="hidden lg:sticky lg:top-4 lg:block lg:h-[calc(100vh-3rem)] lg:overflow-y-auto">
        <CollectionRailContent
          pinned={pinned}
          other={other}
          activeSlug={activeSlug}
          searchActive={searchActive}
          allCount={listPage?.total ?? rowsFromList.length}
          onSelect={(slug) => { setActiveSlug(slug); setSearch(""); setPageLimit(100); }}
        />
      </aside>

      {/* ── Main ────────────────────────────────────────────────── */}
      <main className="min-w-0">
        <Button
          type="button"
          variant="outline"
          className="mb-4 w-full justify-between lg:hidden"
          data-testid="library-mobile-collections-trigger"
          onClick={() => setCollectionsOpen(true)}
        >
          <span className="inline-flex min-w-0 items-center gap-2">
            <Menu className="h-4 w-4 shrink-0" />
            <span className="truncate">Browse collections · {activeSlug ? (collections.find((c) => c.slug === activeSlug)?.name ?? "Collection") : "All assets"}</span>
          </span>
          <span className="font-mono text-xs text-muted-foreground">{inventoryTotal}</span>
        </Button>
        <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Creative Asset Library · {workspace.slug}</div>
            <h1 data-testid="library-title" className="font-display text-3xl font-semibold tracking-tight">
              {searchActive ? `Search "${search}"` : activeSlug ? (collections.find((c) => c.slug === activeSlug)?.name ?? "Collection") : "All assets"}
            </h1>
            <div className="mt-1 font-mono text-[11px] text-muted-foreground">
              {searchActive && searchQ.data ? `${searchQ.data.total} matches` : `${inventoryTotal} assets`}
              {" · "}
              <span data-testid="library-search-provider">{searchQ.data?.search_provider ?? "structured"}</span>
            </div>
          </div>
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <div className="relative w-full sm:w-auto">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="library-search"
                placeholder="Search by keyword, ID, tag..."
                className="w-full pl-7 sm:w-80"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setActiveSlug(null); }}
              />
            </div>
            <div className="flex overflow-hidden rounded-full border border-border/60">
              {(["grid", "list", "lineage"] as ViewMode[]).map((v) => (
                <button
                  key={v}
                  data-testid={`library-view-${v}`}
                  onClick={() => setView(v)}
                  className={`px-3 py-1.5 text-xs font-medium capitalize ${view === v ? "bg-primary text-primary-foreground" : "hover:bg-muted text-muted-foreground"}`}
                >
                  {v === "grid" ? <Grid className="mr-1 inline h-3.5 w-3.5" /> : v === "list" ? <ListIcon className="mr-1 inline h-3.5 w-3.5" /> : <Sparkles className="mr-1 inline h-3.5 w-3.5" />}
                  {v}
                </button>
              ))}
            </div>
          </div>
        </header>

        {(listQ.isLoading || collQ.isLoading) ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-40 w-full" />)}
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border/60 bg-muted/20 py-14 text-center text-sm text-muted-foreground">
            No assets match this view.
          </div>
        ) : view === "grid" ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {rows.map((a) => <AssetCardTile key={a.id} a={a} onOpen={() => setOpenAssetId(a.permanent_asset_id)} />)}
          </div>
        ) : view === "list" ? (
          <AssetList rows={rows} onOpen={(pid) => setOpenAssetId(pid)} />
        ) : (
          <LineageThumbnails rows={rows} workspaceId={workspaceId!} onOpen={(pid) => setOpenAssetId(pid)} />
        )}
        {canLoadMore ? (
          <div className="mt-5 flex justify-center">
            <Button
              data-testid="library-load-more"
              variant="outline"
              onClick={() => setPageLimit((value) => Math.min(value + 100, inventoryTotal))}
              disabled={listQ.isFetching || resolveQ.isFetching}
            >
              {(listQ.isFetching || resolveQ.isFetching) ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Load more · {rows.length} of {inventoryTotal}
            </Button>
          </div>
        ) : null}
      </main>

      <Sheet open={collectionsOpen} onOpenChange={setCollectionsOpen}>
        <SheetContent side="bottom" className="max-h-[82vh] overflow-y-auto rounded-t-xl px-5 pb-8 pt-5" data-testid="library-mobile-collections-drawer">
          <SheetTitle>Browse collections</SheetTitle>
          <SheetDescription>Choose which customer assets appear in the Library.</SheetDescription>
          <div className="mt-5">
            <CollectionRailContent
              pinned={pinned}
              other={other}
              activeSlug={activeSlug}
              searchActive={searchActive}
              allCount={listPage?.total ?? rowsFromList.length}
              onSelect={(slug) => { setActiveSlug(slug); setSearch(""); setPageLimit(100); setCollectionsOpen(false); }}
            />
          </div>
        </SheetContent>
      </Sheet>

      {openAssetId ? (
        <AssetDrawerHost
          workspaceId={workspaceId!}
          pid={openAssetId}
          cardFallback={rowsFromList.find((a) => a.permanent_asset_id === openAssetId) ?? null}
          onClose={() => setOpenAssetId(null)}
          onNavigate={(pid) => setOpenAssetId(pid)}
        />
      ) : null}
    </div>
  );
}

function CollectionRailContent({ pinned, other, activeSlug, searchActive, allCount, onSelect }: {
  pinned: SmartCollection[];
  other: SmartCollection[];
  activeSlug: string | null;
  searchActive: boolean;
  allCount: number;
  onSelect: (slug: string | null) => void;
}): JSX.Element {
  return (
    <>
      <div className="mb-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Smart Collections</div>
      <button
        data-testid="library-collection-all"
        onClick={() => onSelect(null)}
        className={`mb-1 flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm ${activeSlug === null && !searchActive ? "bg-primary/10 text-primary" : "hover:bg-muted"}`}
      >
        <Folder className="h-4 w-4" /> All assets
        <span className="ml-auto font-mono text-[10px]">{allCount}</span>
      </button>
      {pinned.length > 0 ? (
        <div className="mt-2 space-y-0.5">
          {pinned.map((c) => <CollectionButton key={c.slug} c={c} active={activeSlug === c.slug} onClick={() => onSelect(c.slug)} />)}
        </div>
      ) : null}
      <div className="mt-4 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">More</div>
      <div className="mt-1 space-y-0.5">
        {other.map((c) => <CollectionButton key={c.slug} c={c} active={activeSlug === c.slug} onClick={() => onSelect(c.slug)} />)}
      </div>
    </>
  );
}

/**
 * Patch 37.4.43 · Responsive wrapper — routes the drawer to a right-
 * side fixed panel on desktop (unchanged), or a bottom Sheet on
 * mobile (new). Solves the 37.4.42 iter-78 finding "expanding
 * content far below the clicked title" — an ancestor's transform
 * context on mobile was breaking ``position: fixed`` and pushing the
 * drawer below the tile grid. Radix's Portal-based Sheet escapes any
 * transformed ancestor, and the ``vaul``-style overlay makes the
 * scroll model obvious to the founder.
 */
function AssetDrawerHost(props: {
  workspaceId: string;
  pid: string;
  cardFallback: AssetCard | null;
  onClose: () => void;
  onNavigate: (pid: string) => void;
}): JSX.Element {
  const isMobile = useIsMobile();
  if (!isMobile) {
    return <AssetDrawer {...props} />;
  }
  return (
    <Sheet open={true} onOpenChange={(v) => { if (!v) props.onClose(); }}>
      <SheetContent
        side="bottom"
        className="max-h-[90vh] overflow-y-auto rounded-t-xl p-0"
        data-testid="library-asset-drawer-mobile"
      >
        {/* iter-79 a11y fix · Radix requires a Title inside every
            Dialog descendant for screen readers. The drawer's own
            header renders the visible title; this ``sr-only`` copy
            satisfies Radix without duplicating anything visually.
            iter-80 addition · sr-only SheetDescription silences the
            residual "Missing Description or aria-describedby" Radix
            warning without changing what the founder sees. */}
        <SheetTitle className="sr-only">Asset details ({props.pid})</SheetTitle>
        <SheetDescription className="sr-only">
          Preview, review, and manage this asset from the mobile drawer.
        </SheetDescription>
        <AssetDrawer {...props} mobile />
      </SheetContent>
    </Sheet>
  );
}


function CollectionButton({ c, active, onClick }: { c: SmartCollection; active: boolean; onClick: () => void }): JSX.Element {
  const Icon = ICON_MAP[c.icon] ?? Folder;
  return (
    <button
      data-testid={`library-collection-${c.slug}`}
      onClick={onClick}
      className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm ${active ? "bg-primary/10 text-primary" : "hover:bg-muted"}`}
    >
      <Icon className="h-4 w-4 opacity-70" />
      <span className="truncate">{c.name}</span>
      {c.asset_count != null ? (
        <span className="ml-auto font-mono text-[10px] text-muted-foreground">{c.asset_count}</span>
      ) : null}
    </button>
  );
}


function AssetCardTile({ a, onOpen }: { a: AssetCard; onOpen: () => void }): JSX.Element {
  return (
    <button
      data-testid={`library-asset-card-${a.permanent_asset_id}`}
      onClick={onOpen}
      className="group flex min-w-0 flex-col overflow-hidden rounded-lg border border-border/60 bg-card p-3 text-left shadow-sm transition hover:border-primary/40 hover:shadow"
    >
      <div className="flex min-w-0 items-center justify-between gap-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
        <span className="min-w-0 flex-1 truncate text-primary" title={a.permanent_asset_id}>{a.permanent_asset_id}</span>
        <span className="max-w-[45%] shrink-0 truncate" title={a.content_type}>{a.content_type}</span>
      </div>
      <div className="mt-1 line-clamp-2 font-display text-sm font-semibold">
        {a.objective || a.campaign_name || a.excerpt?.slice(0, 60) || "(no title)"}
      </div>
      <div className="mt-1 line-clamp-2 text-[11px] text-muted-foreground">{a.excerpt}</div>
      <div className="mt-2 flex flex-wrap items-center gap-1">
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-medium uppercase tracking-wide ${STATUS_TONE[a.status] || ""}`}>{a.status}</span>
        {(a.tags || []).slice(0, 3).map((t) => (
          <span key={t.label} className="rounded-full bg-muted px-2 py-0.5 text-[9px] font-mono uppercase text-muted-foreground">{t.label}</span>
        ))}
        <span className="ml-auto font-mono text-[9px] text-muted-foreground">v{a.version_number}</span>
      </div>
    </button>
  );
}


function AssetList({ rows, onOpen }: { rows: AssetCard[]; onOpen: (pid: string) => void }): JSX.Element {
  return (
    <div className="min-w-0 rounded-lg border border-border/60 bg-card">
      <div className="divide-y divide-border/40 md:hidden" data-testid="library-mobile-list">
        {rows.map((a) => (
          <button
            key={a.id}
            data-testid={`library-mobile-row-${a.permanent_asset_id}`}
            onClick={() => onOpen(a.permanent_asset_id)}
            className="flex w-full min-w-0 flex-col gap-2 p-4 text-left hover:bg-muted/50"
          >
            <div className="flex min-w-0 items-center justify-between gap-3">
              <span className="min-w-0 flex-1 truncate font-mono text-xs text-primary" title={a.permanent_asset_id}>
                {a.permanent_asset_id}
              </span>
              <span className="max-w-[42%] shrink-0 truncate font-mono text-[10px] uppercase tracking-widest text-muted-foreground" title={a.content_type}>
                {a.content_type}
              </span>
            </div>
            <div className="line-clamp-2 text-sm font-medium">{a.campaign_name || a.objective || "Ungrouped drafts"}</div>
            <div className="flex items-center gap-2">
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase ${STATUS_TONE[a.status] || ""}`}>{a.status}</span>
              <span className="ml-auto font-mono text-xs text-muted-foreground">v{a.version_number}</span>
            </div>
          </button>
        ))}
      </div>
      <div className="hidden max-w-full overflow-x-auto md:block" data-testid="library-desktop-list">
      <table className="w-full text-sm">
        <thead className="border-b border-border/60 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
          <tr><th className="px-3 py-2 text-left">Asset ID</th><th className="px-3 py-2 text-left">Type</th><th className="px-3 py-2 text-left">Campaign</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">v</th></tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <tr
              key={a.id}
              data-testid={`library-row-${a.permanent_asset_id}`}
              onClick={() => onOpen(a.permanent_asset_id)}
              className="cursor-pointer border-b border-border/40 last:border-b-0 hover:bg-muted/50"
            >
              <td className="px-3 py-2 font-mono text-xs text-primary">{a.permanent_asset_id}</td>
              <td className="px-3 py-2">{a.content_type}</td>
              <td className="px-3 py-2 text-muted-foreground">{a.campaign_name || "—"}</td>
              <td className="px-3 py-2"><span className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase ${STATUS_TONE[a.status] || ""}`}>{a.status}</span></td>
              <td className="px-3 py-2 font-mono text-xs text-muted-foreground">v{a.version_number}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}


function LineageThumbnails({ rows, workspaceId, onOpen }: { rows: AssetCard[]; workspaceId: string; onOpen: (pid: string) => void }): JSX.Element {
  return (
    <div className="space-y-3">
      {rows.slice(0, 12).map((a) => (
        <LineageStrip key={a.id} workspaceId={workspaceId} pid={a.permanent_asset_id} card={a} onOpen={() => onOpen(a.permanent_asset_id)} />
      ))}
    </div>
  );
}


function LineageStrip({ workspaceId, pid, card, onOpen }: { workspaceId: string; pid: string; card: AssetCard; onOpen: () => void }): JSX.Element {
  const [q] = useQueries({
    queries: [{ queryKey: ["lib", "lineage", workspaceId, pid], queryFn: () => getLineage(workspaceId, pid) }],
  });
  const nodes = (q.data?.nodes as LineageNode[] | undefined) ?? [];
  return (
    <button
      onClick={onOpen}
      data-testid={`library-lineage-row-${pid}`}
      className="w-full rounded-lg border border-border/60 bg-card p-3 text-left shadow-sm hover:border-primary/40"
    >
      <div className="mb-2 flex items-center justify-between">
        <span className="font-mono text-[11px] text-primary">{pid}</span>
        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{card.content_type}</span>
      </div>
      <div className="flex flex-wrap items-center gap-1 overflow-x-auto">
        {nodes.map((n, i) => (
          <div key={i} className="flex items-center">
            <span
              data-testid={`library-lineage-node-${n.kind}`}
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-wide ${n.at ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}
            >
              {n.kind.replace(/_/g, " ")}
            </span>
            {i < nodes.length - 1 ? <span className="mx-0.5 text-muted-foreground">→</span> : null}
          </div>
        ))}
      </div>
    </button>
  );
}


function AssetDrawer({ workspaceId, pid, cardFallback, onClose, onNavigate, mobile }: {
  workspaceId: string;
  pid: string;
  cardFallback: AssetCard | null;
  onClose: () => void;
  onNavigate: (pid: string) => void;
  /** Patch 37.4.43 · When true, render as a mobile-shaped drawer
   *  inside a Radix Sheet (no fixed positioning; the Sheet handles
   *  layout). When false/undefined, render as the original desktop
   *  right-side fixed drawer. */
  mobile?: boolean;
}): JSX.Element {
  const [tab, setTab] = useState<"overview" | "preview" | "lineage" | "relationships" | "metadata">("overview");
  const [tagInput, setTagInput] = useState("");
  const qc = useQueryClient();
  const [envQ, linQ, relQ, mediaQ] = useQueries({
    queries: [
      { queryKey: ["lib", "env", workspaceId, pid], queryFn: () => getEnvelope(workspaceId, pid) },
      { queryKey: ["lib", "lineage", workspaceId, pid], queryFn: () => getLineage(workspaceId, pid), enabled: tab === "lineage" },
      { queryKey: ["lib", "rel", workspaceId, pid], queryFn: () => getRelationships(workspaceId, pid), enabled: tab === "relationships" },
      // Patch 37.4.18c — narrow-surface media fallback. Always runs so
      // that even if the full envelope endpoint fails in production
      // (Vault drawer blank bug), the Preview tab can still render
      // the known-good rendered image via the tiny /media endpoint.
      { queryKey: ["lib", "media", workspaceId, pid], queryFn: () => getAssetMedia(workspaceId, pid) },
    ],
  });
  const env = envQ.data as AssetEnvelope | undefined;
  // Patch 37.4.18c — When the envelope endpoint fails (production
  // Vault drawer blank bug: MO-IMG-000083), fall back to the AssetCard
  // the list endpoint already gave us. That way the drawer NEVER
  // renders "silently blank" — the founder always sees the pid,
  // content_type, campaign, and status they were promised.
  const envErr = envQ.error as ApiError | Error | undefined;
  const envelopeFailed = envQ.isError && !!envErr;
  const head: any = env?.head ?? (cardFallback ? {
    permanent_asset_id: cardFallback.permanent_asset_id,
    content_type: cardFallback.content_type,
    status: cardFallback.status,
    objective: cardFallback.objective,
    campaign: cardFallback.campaign_name,
    campaign_pack_id: cardFallback.campaign_pack_id,
    version_number: cardFallback.version_number,
    media_file_ids: cardFallback.media_file_ids,
    body_markdown: cardFallback.excerpt,
    tags: cardFallback.tags,
    provider: cardFallback.provider,
    model: cardFallback.model,
    brand_brain_version: cardFallback.brand_brain_version,
    // Patch 37.4.21 — lifecycle discriminators propagate from the
    // list card so the CTA swap survives an envelope-endpoint failure
    // (the exact production symptom that produced this blocker).
    payload_maturity: cardFallback.payload_maturity,
    capability: cardFallback.capability,
  } : null);
  const packId = head?.campaign_pack_id;
  // Patch 37.4.42 · Playbook exportability comes from the backend.
  // The rule (see ``services/pack_stats_service::is_exportable_playbook``)
  // is: not-a-Quick-Pack + pack.status != generating + at least one
  // head in {approved, scheduled, published}. Envelope surfaces it
  // via ``campaign_pack.is_exportable_playbook``; the list card
  // surfaces it via ``is_exportable_playbook`` for the fallback path.
  const packExportable = Boolean(
    (env?.campaign_pack as any)?.is_exportable_playbook
      ?? cardFallback?.is_exportable_playbook
      ?? false,
  );

  const tagMut = useMutation({
    mutationFn: (tags: string[]) => assignTags(workspaceId, pid, tags),
    onSuccess: () => { toast.success("Tags saved"); qc.invalidateQueries({ queryKey: ["lib"] }); setTagInput(""); },
    onError: (e: Error) => toast.error(e.message),
  });
  const exportMut = useMutation({
    mutationFn: () => exportPlaybook(workspaceId, packId!),
    onSuccess: (p: Playbook) => {
      toast.success(`Playbook ${p.permanent_asset_id} ready (${p.renderer})`);
      if (p.file_signed_url) window.open(p.file_signed_url, "_blank");
      qc.invalidateQueries({ queryKey: ["lib"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const uploadMut = useMutation({
    mutationFn: (file: File) => uploadFile(workspaceId, pid, file),
    onSuccess: () => { toast.success("File uploaded"); qc.invalidateQueries({ queryKey: ["lib"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div
      data-testid="library-asset-drawer"
      className={
        mobile
          // Mobile: no fixed positioning — Sheet handles layout.
          ? "flex w-full flex-col bg-card"
          // Desktop: original right-side fixed drawer.
          : "fixed inset-y-0 right-0 z-40 flex w-full max-w-2xl flex-col border-l border-border/60 bg-card shadow-2xl"
      }
    >
      <div className="flex items-start justify-between border-b border-border/60 px-5 py-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-primary">{pid}</div>
          <div className="font-display text-sm font-semibold">{head?.objective || head?.campaign || head?.content_type || "Asset"}</div>
          <div className="mt-1 flex items-center gap-1">
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase ${STATUS_TONE[head?.status] || ""}`}>{head?.status}</span>
            {(head?.tags || []).map((t: any) => (
              <span key={t.label} data-testid={`library-tag-chip-${t.label}`} className="rounded-full bg-muted px-2 py-0.5 text-[9px] font-mono uppercase text-muted-foreground">{t.label}</span>
            ))}
          </div>
        </div>
        <button
          onClick={onClose}
          data-testid="library-drawer-close"
          // Patch 37.4.43 iter-79 · When the drawer is inside a
          // mobile Sheet, Radix's SheetContent renders its own close
          // X at ``right-4 top-4`` overlapping this one and
          // intercepting pointer events. Hide the drawer's own close
          // on mobile — the Sheet X (and swipe-down / escape) already
          // provides the affordance.
          className={mobile
            ? "hidden"
            : "rounded-full p-1.5 text-muted-foreground hover:bg-muted"}
        >
          ✕
        </button>
      </div>

      <div className="flex gap-1 border-b border-border/60 px-3">
        {(["overview", "preview", "lineage", "relationships", "metadata"] as const).map((t) => (
          <button
            key={t}
            data-testid={`library-tab-${t}`}
            onClick={() => setTab(t)}
            className={`border-b-2 px-2 py-2 text-xs font-medium capitalize ${tab === t ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 text-sm">
        {envelopeFailed ? (
          <div
            data-testid="library-envelope-error"
            className="mb-3 rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-xs"
          >
            <div className="font-semibold text-amber-800 dark:text-amber-200">
              Couldn&apos;t load full asset envelope
              {envErr instanceof ApiError ? ` (HTTP ${envErr.status} · ${envErr.code})` : ""}
            </div>
            <div className="mt-1 text-amber-900/80 dark:text-amber-100/80">
              Showing partial info from the library listing. Refresh or reopen the drawer to retry.
            </div>
            {envErr instanceof ApiError && envErr.correlationId ? (
              <div
                className="mt-1 font-mono text-[10px] text-amber-900/60 dark:text-amber-100/60"
                data-testid="library-envelope-error-cid"
              >
                cid: {envErr.correlationId}
              </div>
            ) : null}
            {envErr instanceof ApiError && envErr.message ? (
              <div className="mt-1 text-[10px] text-amber-900/60 dark:text-amber-100/60">
                {envErr.message}
              </div>
            ) : null}
          </div>
        ) : null}
        {tab === "overview" ? <OverviewTab env={env} head={head} /> : null}
        {tab === "preview" ? (
          <PreviewTab
            head={head}
            files={
              (env?.files && env.files.length > 0)
                ? env.files
                : ((mediaQ.data?.files as any[]) ?? [])
            }
          />
        ) : null}
        {tab === "lineage" ? <LineageTab nodes={(linQ.data?.nodes as LineageNode[]) ?? []} /> : null}
        {tab === "relationships" ? <RelationshipsTab rel={relQ.data as Relationships | undefined} onNavigate={onNavigate} /> : null}
        {tab === "metadata" ? <MetadataTab env={env} head={head} pid={pid} /> : null}
      </div>

      <div className="flex flex-wrap gap-2 border-t border-border/60 px-5 py-3">
        {/* Patch 37.4.21 — When the current head is a rendered
            deliverable sitting in ``needs_review``, replace the
            pack-level Export Campaign Playbook CTA with an
            asset-level "Review Final Media →" that routes into
            the guided Review Session for the parent pack. Export
            Campaign Playbook is not the primary action for a
            final-media review state — approving the render is.

            Patch 37.4.22 — For a ``video_production_kit`` head we
            consume the canonical lifecycle recommendation
            (``asset_lifecycle_service.derive_stage``) directly in
            the drawer:
              * approved kit WITHOUT ``hero_asset_id``  → Choose Starting Frame
              * approved kit WITH ``hero_asset_id``     → Create Video
              * draft / needs_review kit                → Review Kit
            Never Schedule and never Export Campaign Playbook for a
            kit — those are downstream / pack-level actions and this
            is where the invariant was being violated in production. */}
        {(() => {
          if (!packId) return null;
          const status = head?.status as string | undefined;
          const contentType = head?.content_type as string | undefined;
          const payloadMaturity = head?.payload_maturity as string | undefined;
          const heroAssetId =
            head?.body_json?.hero_asset_id ?? head?.body?.hero_asset_id ?? null;

          // Rendered deliverable in needs_review — Patch 37.4.21 CTA.
          if (payloadMaturity === "rendered_deliverable" && status === "needs_review") {
            // Patch 37.4.22b · Preserve the initiating asset identity
            // across the pack-level route by encoding it as a query
            // parameter. Review Session parses ``?asset=`` and seeds
            // the cursor to that item.
            //
            // Patch 37.4.43 iter-80 · Anchor on the head's INTERNAL
            // ``id`` (never redacted) instead of ``permanent_asset_id``
            // (null-scrubbed for non-founder viewers via Patch 34.1.6).
            // The queue matcher's ``asset_id`` fallback resolves for
            // every viewer class; the previous shape only worked for
            // founders.
            const initiatingId =
              (head?.id as string | undefined)
              ?? (env?.head?.id as string | undefined)
              ?? "";
            const suffix = initiatingId
              ? `?asset=${encodeURIComponent(initiatingId)}`
              : "";
            return (
              <Link
                to={`/campaigns/${packId}/review-session${suffix}`}
                data-testid={`library-drawer-review-final-media-cta-${packId}`}
                className="flex-1 min-w-[140px] inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
                onClick={onClose}
              >
                Review Final Media →
              </Link>
            );
          }

          // Video production kit — Patch 37.4.22 lifecycle-driven CTA.
          if (contentType === "video_production_kit" && payloadMaturity !== "rendered_deliverable") {
            if (status === "draft" || status === "needs_review") {
              return (
                <Link
                  to={`/campaigns/${packId}/review-session`}
                  data-testid={`library-drawer-review-kit-cta-${packId}`}
                  className="flex-1 min-w-[140px] inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
                  onClick={onClose}
                >
                  Review Kit →
                </Link>
              );
            }
            if (status === "approved") {
              const ctaLabel = heroAssetId ? "Create Video →" : "Choose Starting Frame →";
              const ctaTestId = heroAssetId
                ? `library-drawer-create-video-cta-${packId}`
                : `library-drawer-choose-hero-cta-${packId}`;
              return (
                <Link
                  to={`/ai-studio?asset=${env?.head?.id ?? ""}`}
                  data-testid={ctaTestId}
                  className="flex-1 min-w-[140px] inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
                  onClick={onClose}
                >
                  {ctaLabel}
                </Link>
              );
            }
            // Kit in scheduled/published/archived — no primary progression
            // CTA. Return null so the drawer shows no misleading action;
            // the metadata tab still surfaces the lifecycle state.
            return null;
          }

          // Default: pack-level actions.
          // Patch 37.4.43 · When the current head is a NEEDS_REVIEW
          // asset that doesn't match any of the specialised CTAs
          // above (rendered image / kit), route the founder into the
          // Review Session for the initiating asset so the "MO-SOC-
          // 000164 shows needs_review but the queue is empty" symptom
          // from 37.4.42 iter-78 cannot recur: the CTA is anchored to
          // THIS asset's ``campaign_pack_id`` and its internal ``id``
          // — the queue query cannot mismatch.
          //
          // iter-79 fix (HIGH): ``permanent_asset_id`` is REDACTED to
          // null in the review-queue payload for non-founder viewers
          // (Patch 34.1.6 scrub), so the ReviewSessionPage matcher
          // could never find a queue item keyed on it. The internal
          // ``id`` is preserved for every viewer, and the matcher
          // accepts either — anchor on ``id`` so the deep link
          // works for customers, not only founders.
          if (status === "needs_review") {
            const initiatingId =
              (head?.id as string | undefined)
              ?? (env?.head?.id as string | undefined)
              ?? "";
            const suffix = initiatingId
              ? `?asset=${encodeURIComponent(initiatingId)}`
              : "";
            return (
              <Link
                to={`/campaigns/${packId}/review-session${suffix}`}
                data-testid={`library-drawer-open-review-session-${packId}`}
                className="flex-1 min-w-[140px] inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
                onClick={onClose}
              >
                Open Review Session →
              </Link>
            );
          }

          // Patch 37.4.42 · Only render Export Campaign Playbook when
          // the pack can truthfully produce a playbook — Quick Pack
          // sentinel + generating + no exportable-status head are
          // all filtered out server-side.
          if (!packExportable) return null;
          return (
            <Button data-testid={`library-export-playbook-${packId}`} onClick={() => exportMut.mutate()} disabled={exportMut.isPending} className="flex-1 min-w-[140px]">
              {exportMut.isPending ? <><Loader2 className="mr-1 inline h-3 w-3 animate-spin" /> Rendering…</> : "Export Campaign Playbook"}
            </Button>
          );
        })()}
        <div className="flex items-center gap-1">
          <Input
            data-testid="library-tag-add"
            placeholder="add tag…"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && tagInput.trim()) tagMut.mutate([tagInput.trim()]); }}
            className="w-28"
          />
          <label className="cursor-pointer rounded-md border border-border/60 bg-background px-3 py-1.5 text-xs" data-testid="library-upload">
            Upload
            <input type="file" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadMut.mutate(f); }} />
          </label>
        </div>
      </div>
    </div>
  );
}


function OverviewTab({ env, head }: { env: AssetEnvelope | undefined; head: any }): JSX.Element {
  // Patch 37.4.18c — accept a ``head`` fallback derived from the
  // AssetCard so the Overview tab is never blank when the envelope
  // endpoint fails. ``env`` is still used for ``versions.length`` when
  // available.
  const h = env?.head ?? head;
  if (!h) return <Skeleton className="h-32 w-full" />;
  const versionCount = env?.versions?.length ?? h.version_number ?? "—";
  return (
    <div className="space-y-3" data-testid="library-overview-tab">
      {h.body_markdown ? (
        <div data-testid="library-body" className="rounded-md border border-border/40 bg-muted/20 p-3 text-xs leading-relaxed whitespace-pre-wrap">
          {h.body_markdown}
        </div>
      ) : null}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <Kv label="Content type">{h.content_type || "—"}</Kv>
        <Kv label="Campaign">{h.campaign || h.campaign_name || "—"}</Kv>
        <Kv label="Provider">{h.provider || "—"} / {h.model || "—"}</Kv>
        <Kv label="Brand version">{h.brand_brain_version || "—"}</Kv>
        <Kv label="Versions">{versionCount}</Kv>
        <Kv label="Cost so far">${Number(h.estimated_cost_usd ?? 0).toFixed(4)}</Kv>
      </div>
    </div>
  );
}


function PreviewTab({ head, files }: { head: any; files: any[] }): JSX.Element {
  const ct = head?.content_type as string | undefined;
  // Patch 37.4.18 (Vault drawer fix) — prefer the server-minted 15-min
  // signed URL that ``build_asset_envelope`` attaches per file. It
  // works for R2 (production) and local storage (dev). The bare
  // ``/api/v1/library/local-media/{key}`` fallback is preserved for
  // pre-signed-URL assets in local dev only — it returns 404 in
  // production because ``storage.kind != "local"``.
  const backend = config.backendUrl;
  const mediaUrl = (f: any): string =>
    (typeof f?.signed_url === "string" && f.signed_url) ||
    `${backend}/api/v1/library/local-media/${f?.storage_key ?? ""}`;
  const fileKey = (f: any, i: number): string =>
    String(f?.id ?? f?._id ?? f?.storage_key ?? i);
  // A rendered deliverable is authoritatively identified by
  // ``payload_maturity`` (not by ``content_type``, which is inherited
  // from the v1 spec, e.g. ``image_prompt_social`` on a v2 render).
  const isRenderedDeliverable = head?.payload_maturity === "rendered_deliverable";
  const imageFiles = files.filter((f) => (f?.mime_type ?? "").toLowerCase().startsWith("image/"));
  const videoFiles = files.filter((f) => (f?.mime_type ?? "").toLowerCase().startsWith("video/"));
  const pdfFile = files.find((f) => f?.mime_type === "application/pdf");
  if (ct === "campaign_playbook" && pdfFile) {
    return (
      <div data-testid="library-playbook-preview" className="space-y-2">
        <embed src={mediaUrl(pdfFile)} type="application/pdf" className="h-[600px] w-full rounded-md border border-border/60" />
      </div>
    );
  }
  if (isRenderedDeliverable && videoFiles.length > 0) {
    return (
      <div className="space-y-2" data-testid="library-rendered-video-preview">
        {videoFiles.map((f, i) => (
          <video key={fileKey(f, i)} src={mediaUrl(f)} controls className="w-full rounded-md border border-border/60" />
        ))}
      </div>
    );
  }
  if (isRenderedDeliverable && imageFiles.length > 0) {
    return (
      <div className="space-y-2" data-testid="library-rendered-image-preview">
        {imageFiles.map((f, i) => (
          <img
            key={fileKey(f, i)}
            src={mediaUrl(f)}
            alt={head?.title || head?.objective || "Rendered image"}
            className="w-full rounded-md border border-border/60"
            data-testid={`library-preview-image-${fileKey(f, i)}`}
          />
        ))}
        {head?.image_prompt ? (
          <details className="rounded-md border border-border/40 bg-muted/20 p-3 text-xs">
            <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Source prompt</summary>
            <div className="mt-2 whitespace-pre-wrap">{head.image_prompt}</div>
          </details>
        ) : null}
      </div>
    );
  }
  if (ct?.startsWith("image_prompt_")) {
    return (
      <div className="space-y-2">
        <div className="rounded-md border border-border/40 bg-muted/20 p-3 text-xs whitespace-pre-wrap">
          {head?.image_prompt || head?.body_markdown || "(no prompt)"}
        </div>
        {imageFiles.map((f, i) => (
          <img key={fileKey(f, i)} src={mediaUrl(f)} className="rounded-md border border-border/60" />
        ))}
      </div>
    );
  }
  return (
    <div className="rounded-md border border-border/40 bg-muted/20 p-3 text-xs leading-relaxed whitespace-pre-wrap">
      {head?.body_markdown || "(no preview available for this content type)"}
    </div>
  );
}


function LineageTab({ nodes }: { nodes: LineageNode[] }): JSX.Element {
  return (
    <ol data-testid="library-lineage" className="relative border-l-2 border-primary/30 pl-4 space-y-2">
      {nodes.map((n) => (
        <li key={n.kind} data-testid={`library-lineage-node-${n.kind}`} className={`rounded-md border p-2 text-xs ${n.at ? "border-primary/40 bg-primary/5" : "border-dashed border-border/60 bg-muted/20"}`}>
          <div className="font-mono text-[10px] uppercase tracking-widest text-primary">{n.kind.replace(/_/g, " ")}</div>
          <div className="font-semibold">{n.label || "—"}</div>
          {n.at ? <div className="text-[10px] text-muted-foreground">{n.at}</div> : null}
        </li>
      ))}
    </ol>
  );
}


function RelationshipsTab({ rel, onNavigate }: { rel: Relationships | undefined; onNavigate: (pid: string) => void }): JSX.Element {
  if (!rel) return <Skeleton className="h-20 w-full" />;
  const Section = ({ title, items }: { title: string; items: string[] }) => (
    <div className="mt-3">
      <div className="mb-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{title}</div>
      {items.length === 0 ? <div className="text-[11px] text-muted-foreground">—</div> :
        <div className="flex flex-wrap gap-1">{items.map((pid) => (
          <button key={pid} data-testid={`library-related-${pid}`} onClick={() => onNavigate(pid)} className="rounded-full bg-muted px-2 py-0.5 font-mono text-[10px] text-primary hover:bg-primary/10">
            {pid}
          </button>
        ))}</div>}
    </div>
  );
  return (
    <div>
      <Section title="Parent" items={rel.parent ? [rel.parent] : []} />
      <Section title="Siblings (same campaign)" items={rel.siblings} />
      <Section title="Derivatives" items={rel.derivatives} />
      <Section title="References" items={rel.references.map((r) => `${r.kind}:${r.target_permanent_asset_id}`)} />
      <Section title="Translations" items={rel.translations} />
    </div>
  );
}


function MetadataTab({ env, head, pid }: { env: AssetEnvelope | undefined; head: any; pid: string }): JSX.Element {
  // Patch 37.4.18c — accept ``head`` fallback (from AssetCard) so
  // metadata isn't a blank skeleton when the envelope endpoint fails.
  const h = env?.head ?? head;
  if (!h) return <Skeleton className="h-32 w-full" />;
  return (
    <div data-testid="library-metadata" className="grid grid-cols-2 gap-2 font-mono text-[11px]">
      <Kv label="Asset ID">{h.permanent_asset_id || pid}</Kv>
      <Kv label="Workspace">{env?.workspace_id || "—"}</Kv>
      <Kv label="Pack">{h.campaign_pack_id || "—"}</Kv>
      <Kv label="Brief">{h.creative_brief_id || "—"}</Kv>
      <Kv label="Prompt">{h.prompt_key || "—"}@{h.prompt_version || "—"}</Kv>
      <Kv label="Content hash">{h.fingerprint?.content_hash || "—"}</Kv>
      <Kv label="Generation hash">{h.fingerprint?.generation_hash || "—"}</Kv>
      <Kv label="Times scheduled">{h.usage?.times_scheduled ?? 0}</Kv>
      <Kv label="Times published">{h.usage?.times_published ?? 0}</Kv>
      <Kv label="Regenerations">{h.usage?.regeneration_count ?? 0}</Kv>
      <Kv label="Derivatives">{h.usage?.derivative_count ?? 0}</Kv>
      <Kv label="Exports">{h.usage?.export_count ?? 0}</Kv>
    </div>
  );
}


function Kv({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
      <span className="truncate">{children}</span>
    </div>
  );
}
